import React, { useState } from 'react';
import { FileText, Download, ExternalLink, Search, BookOpen, Clipboard, Shield, AlertCircle } from 'lucide-react';

// ============================================================
// GOOGLE DOCS / DRIVE LINKS
// Replace googleDriveLink values with your actual shared URLs.
// For Google Docs: File → Share → "Anyone with the link" → Copy link
// For PDFs in Drive: Right-click → Get link → "Anyone with link"
// ============================================================

const DOCUMENTS = [
  {
    id: 1,
    name: 'Preferred Provider Agreement',
    desc: 'Master agreement for Neurogenx Preferred Providers. Review before countersigning.',
    category: 'Contracts',
    type: 'PDF',
    updated: 'Jan 15, 2026',
    icon: Shield,
    iconClass: 'pdf',
    googleDriveLink: 'https://docs.google.com/document/d/YOUR_DOC_ID/view',
    required: true,
  },
  {
    id: 2,
    name: 'Patient Intake Form',
    desc: 'Standard intake form for new NGX patients. Print or use digitally.',
    category: 'Clinical Forms',
    type: 'PDF',
    updated: 'Feb 1, 2026',
    icon: Clipboard,
    iconClass: 'form',
    googleDriveLink: 'https://drive.google.com/file/d/YOUR_FILE_ID/view',
    required: false,
  },
  {
    id: 3,
    name: 'Informed Consent — Electrical Stimulation',
    desc: 'HIPAA-compliant consent form. Must be signed prior to first treatment.',
    category: 'Clinical Forms',
    type: 'PDF',
    updated: 'Jan 2026',
    icon: FileText,
    iconClass: 'pdf',
    googleDriveLink: 'https://drive.google.com/file/d/YOUR_FILE_ID/view',
    required: true,
  },
  {
    id: 4,
    name: 'Treatment Protocol Guide',
    desc: 'Step-by-step clinical protocols for all NGX device models.',
    category: 'Clinical Guides',
    type: 'Google Doc',
    updated: 'Feb 10, 2026',
    icon: BookOpen,
    iconClass: 'doc',
    googleDriveLink: 'https://docs.google.com/document/d/YOUR_DOC_ID/view',
    required: false,
  },
  {
    id: 5,
    name: 'Device Calibration Checklist',
    desc: 'Monthly calibration checklist for NGX device maintenance compliance.',
    category: 'Clinical Guides',
    type: 'PDF',
    updated: 'Dec 2025',
    icon: Clipboard,
    iconClass: 'form',
    googleDriveLink: 'https://drive.google.com/file/d/YOUR_FILE_ID/view',
    required: false,
  },
  {
    id: 6,
    name: 'ICD-10 Billing Reference Sheet',
    desc: 'Recommended ICD-10 codes for NGX treatments and related diagnoses.',
    category: 'Billing',
    type: 'PDF',
    updated: 'Jan 2026',
    icon: FileText,
    iconClass: 'doc',
    googleDriveLink: 'https://drive.google.com/file/d/YOUR_FILE_ID/view',
    required: false,
  },
  {
    id: 7,
    name: 'Insurance Pre-Auth Letter Template',
    desc: 'Template letter for submitting prior authorization to insurance carriers.',
    category: 'Billing',
    type: 'Google Doc',
    updated: 'Jan 2026',
    icon: FileText,
    iconClass: 'doc',
    googleDriveLink: 'https://docs.google.com/document/d/YOUR_DOC_ID/view',
    required: false,
  },
  {
    id: 8,
    name: 'Provider Onboarding Checklist',
    desc: 'Complete checklist to ensure your practice is fully set up for NGX treatments.',
    category: 'Onboarding',
    type: 'Google Doc',
    updated: 'Feb 2026',
    icon: Clipboard,
    iconClass: 'form',
    googleDriveLink: 'https://docs.google.com/document/d/YOUR_DOC_ID/view',
    required: false,
  },
  {
    id: 9,
    name: 'W-9 Form — Submission',
    desc: 'Submit your practice W-9 for vendor records. Upload completed form to the portal.',
    category: 'Admin',
    type: 'PDF',
    updated: 'Ongoing',
    icon: FileText,
    iconClass: 'pdf',
    googleDriveLink: 'https://www.irs.gov/pub/irs-pdf/fw9.pdf',
    required: false,
  },
];

const CATEGORIES = ['All', 'Clinical Forms', 'Clinical Guides', 'Contracts', 'Billing', 'Onboarding', 'Admin'];

export default function DocumentsPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');

  const filtered = DOCUMENTS.filter(d => {
    const matchCat = activeCategory === 'All' || d.category === activeCategory;
    const matchSearch = d.name.toLowerCase().includes(search.toLowerCase()) ||
      d.desc.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const handleOpen = (doc) => {
    if (!doc.googleDriveLink.includes('YOUR_DOC_ID') && !doc.googleDriveLink.includes('YOUR_FILE_ID')) {
      window.open(doc.googleDriveLink, '_blank');
    } else {
      alert('This document link has not been configured yet.\n\nReplace the googleDriveLink in DocumentsPage.jsx with your actual Google Drive/Docs shared URL.');
    }
  };

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">Documents & Forms</div>
          <div className="page-subtitle">Clinical forms, contracts, guides, and billing references</div>
        </div>
      </div>

      <div className="page-body">
        {/* INFO */}
        <div className="alert alert-info mb-16">
          <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            Documents open in Google Drive/Docs. Replace placeholder links in <code>DocumentsPage.jsx</code> with your actual Google Drive shared URLs.
          </span>
        </div>

        {/* SEARCH */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              className="form-input"
              placeholder="Search documents..."
              style={{ paddingLeft: 32 }}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <span style={{ fontSize: 12, color: '#64748b' }}>{filtered.length} documents</span>
        </div>

        {/* TABS */}
        <div className="tabs">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              className={`tab ${activeCategory === cat ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* DOCUMENTS LIST */}
        <div className="docs-grid">
          {filtered.map(doc => {
            const Icon = doc.icon;
            return (
              <div key={doc.id} className="doc-card">
                <div className={`doc-icon ${doc.iconClass}`}>
                  <Icon size={20} />
                </div>
                <div className="doc-info">
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    <div className="doc-name">{doc.name}</div>
                    {doc.required && (
                      <span className="badge badge-pink" style={{ flexShrink: 0, marginTop: 1 }}>Required</span>
                    )}
                  </div>
                  <div className="doc-desc">{doc.desc}</div>
                  <div className="doc-meta">
                    <span className="badge badge-primary" style={{ marginRight: 8 }}>{doc.type}</span>
                    Updated: {doc.updated}
                  </div>
                  <div className="doc-actions">
                    <button className="btn btn-primary btn-sm" onClick={() => handleOpen(doc)}>
                      <ExternalLink size={12} /> Open
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleOpen(doc)}>
                      <Download size={12} /> Download
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
            <FileText size={40} style={{ marginBottom: 12 }} />
            <div style={{ fontWeight: 500 }}>No documents found</div>
          </div>
        )}

        {/* UPLOAD CTA */}
        <div style={{
          marginTop: 32, background: 'white', border: '1px dashed #cbd5e1',
          borderRadius: 10, padding: '20px 24px', textAlign: 'center'
        }}>
          <div style={{ fontWeight: 500, fontSize: 14, color: '#475569' }}>
            Need to submit a document to Neurogenx?
          </div>
          <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4, marginBottom: 12 }}>
            Use our secure upload form for W-9s, signed agreements, or other required paperwork.
          </div>
          <a href="mailto:providers@neurogenx.com?subject=Document Submission" className="btn btn-secondary">
            Submit via Email
          </a>
        </div>
      </div>
    </>
  );
}
